import { useCallback, useMemo, useRef, useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  ButtonBase,
  Button,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddIcon from '@mui/icons-material/Add'
import BugReportIcon from '@mui/icons-material/BugReport'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import type { SessionInfoResponse } from '../types'
import {
  formatDuration,
  sessionDuration,
  sessionKey,
  startOfWeekMonday,
} from '../lib/sessionTime'

// ─── Accents ──────────────────────────────────────────────────────────────────
// Three meanings, three hues. Everything else in the calendar is greyscale so the
// coloured bars are the only thing the eye has to parse.

const ACCENT = {
  session:  '#4285F4',  // any on-call shift, rostered or an override — they read the same
  incident: '#EA4335',  // an incident with no matching shift
} as const

type SessionKind = keyof typeof ACCENT

function kindOf(s: SessionInfoResponse): SessionKind {
  return s.sessionId ? 'session' : 'incident'
}

// ─── Day segments ─────────────────────────────────────────────────────────────
// A shift that runs 21:00 → 09:00 shows up on both calendar days, clipped to each.

interface DaySegment {
  key: string
  session: SessionInfoResponse
  kind: SessionKind
  /** Clipped to the day — what actually happens on this date. */
  from: Dayjs
  to: Dayjs
  continuesBefore: boolean
  continuesAfter: boolean
  incidentCount: number
}

/** Maps `YYYY-MM-DD` → the shift slices happening on that date, in start order. */
function buildSegments(sessions: SessionInfoResponse[], tz: string): Map<string, DaySegment[]> {
  const byDay = new Map<string, DaySegment[]>()

  for (const session of sessions) {
    const start = dayjs(session.startTime).tz(tz)
    const end = session.endTime ? dayjs(session.endTime).tz(tz) : start
    // A shift ending exactly at midnight belongs to the previous day only.
    const lastDay = end.isAfter(start) && end.isSame(end.startOf('day'))
      ? end.subtract(1, 'millisecond').startOf('day')
      : end.startOf('day')

    const kind = kindOf(session)
    const incidentDays = (session.incidents ?? []).map((i) => dayjs(i.startedAt).tz(tz).format('YYYY-MM-DD'))

    for (let d = start.startOf('day'); !d.isAfter(lastDay); d = d.add(1, 'day')) {
      const iso = d.format('YYYY-MM-DD')
      const segment: DaySegment = {
        key: `${sessionKey(session)}@${iso}`,
        session,
        kind,
        from: d.isSame(start, 'day') ? start : d.startOf('day'),
        to: d.isSame(end, 'day') ? end : d.endOf('day'),
        continuesBefore: !d.isSame(start, 'day'),
        continuesAfter: !d.isSame(lastDay, 'day'),
        incidentCount: incidentDays.filter((day) => day === iso).length,
      }
      const bucket = byDay.get(iso)
      if (bucket) bucket.push(segment)
      else byDay.set(iso, [segment])
    }
  }

  for (const bucket of byDay.values()) {
    bucket.sort((a, b) => a.from.valueOf() - b.from.valueOf())
  }
  return byDay
}

// ─── Session bar ──────────────────────────────────────────────────────────────

function SessionBar({
  segment,
  compact,
  tz,
  onOpen,
}: {
  segment: DaySegment
  compact: boolean
  tz: string
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const accent = ACCENT[segment.kind]
  const label = segment.session.sessionId
    ? (segment.session.title ?? t('sessions.dialog.onCallSession'))
    : t('sessions.table.unmatchedIncident')
  const range = segment.session.endTime
    ? `${segment.continuesBefore ? '…' : segment.from.format('HH:mm')} – ${segment.continuesAfter ? '…' : segment.to.format('HH:mm')}`
    : segment.from.format('HH:mm')
  // Every shift carries the same "<me> - <policy>" title, so repeating it in each cell is
  // pure noise. Only the odd one out — an incident with no shift — needs saying.
  const caption = segment.kind === 'incident' ? t('sessions.table.unmatchedIncident') : ''

  const tooltip = (
    <Box>
      <Typography variant="caption" fontWeight={700} display="block">{label}</Typography>
      <Typography variant="caption" display="block">
        {segment.session.endTime
          ? `${dayjs(segment.session.startTime).tz(tz).format('DD MMM HH:mm')} – ${dayjs(segment.session.endTime).tz(tz).format('DD MMM HH:mm')}`
          : dayjs(segment.session.startTime).tz(tz).format('DD MMM HH:mm')}
      </Typography>
      {segment.incidentCount > 0 && (
        <Typography variant="caption" display="block">
          {t('sessions.calendar.incidentCount', { count: segment.incidentCount })}
        </Typography>
      )}
    </Box>
  )

  return (
    <Tooltip title={tooltip} placement="top" disableInteractive enterDelay={400}>
      <ButtonBase
        focusRipple
        onClick={(e) => { e.stopPropagation(); onOpen() }}
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 0.5,
          px: compact ? 0 : 0.625,
          height: compact ? 7 : 20,
          borderRadius: compact ? 4 : '4px',
          bgcolor: alpha(accent, compact ? 0.5 : 0.18),
          color: accent,
          borderLeft: compact ? 'none' : `3px solid ${accent}`,
          // Rounded only on the edge where the shift actually begins / ends.
          borderTopLeftRadius: segment.continuesBefore ? 1 : undefined,
          borderBottomLeftRadius: segment.continuesBefore ? 1 : undefined,
          borderTopRightRadius: segment.continuesAfter ? 1 : undefined,
          borderBottomRightRadius: segment.continuesAfter ? 1 : undefined,
          overflow: 'hidden',
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: alpha(accent, compact ? 0.7 : 0.32) },
        }}
      >
        {!compact && (
          <Typography
            component="span"
            sx={{
              fontSize: 11, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap',
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {range}
          </Typography>
        )}
        {!compact && (
          <Typography
            component="span"
            sx={{
              fontSize: 11, flex: 1, minWidth: 0, opacity: 0.72, textAlign: 'left',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {caption}
          </Typography>
        )}
        {segment.incidentCount > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              ml: compact ? 'auto' : 0,
              mr: compact ? 0.25 : 0,
              width: compact ? 5 : 14,
              height: compact ? 5 : 14,
              borderRadius: '50%',
              bgcolor: ACCENT.incident,
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: compact ? undefined : '14px',
              textAlign: 'center',
            }}
          >
            {compact ? '' : segment.incidentCount}
          </Box>
        )}
      </ButtonBase>
    </Tooltip>
  )
}

// ─── Day cell ─────────────────────────────────────────────────────────────────

function DayCell({
  date,
  segments,
  inMonth,
  isToday,
  isSelected,
  compact,
  maxBars,
  tz,
  onSelect,
  onOpenSession,
}: {
  date: Dayjs
  segments: DaySegment[]
  inMonth: boolean
  isToday: boolean
  isSelected: boolean
  compact: boolean
  maxBars: number
  tz: string
  onSelect: () => void
  onOpenSession: (s: SessionInfoResponse) => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dark = theme.palette.mode === 'dark'
  const isWeekend = date.day() === 0 || date.day() === 6

  const visible = segments.slice(0, maxBars)
  const hidden = segments.length - visible.length

  return (
    <Box
      onClick={onSelect}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        minHeight: compact ? 76 : 112,
        p: compact ? 0.5 : 0.75,
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '3px' : '2px',
        bgcolor: isWeekend
          ? (dark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.018)')
          : 'transparent',
        opacity: inMonth ? 1 : 0.38,
        boxShadow: isSelected ? `inset 0 0 0 2px ${theme.palette.primary.main}` : 'none',
        transition: 'background-color 120ms ease, box-shadow 120ms ease',
        '&:hover': { bgcolor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)' },
      }}
    >
      <Box
        sx={{
          alignSelf: 'flex-start',
          minWidth: compact ? 18 : 22,
          height: compact ? 18 : 22,
          px: 0.5,
          borderRadius: '999px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: compact ? 11 : 12,
          fontWeight: isToday ? 700 : 500,
          bgcolor: isToday ? 'primary.main' : 'transparent',
          color: isToday ? '#fff' : 'text.secondary',
        }}
      >
        {date.date()}
      </Box>

      {visible.map((seg) => (
        <SessionBar
          key={seg.key}
          segment={seg}
          compact={compact}
          tz={tz}
          onOpen={() => onOpenSession(seg.session)}
        />
      ))}

      {hidden > 0 && (
        <Typography
          sx={{ fontSize: compact ? 9 : 10, color: 'text.secondary', pl: compact ? 0 : 0.5, lineHeight: 1.4 }}
        >
          {t('sessions.calendar.more', { count: hidden })}
        </Typography>
      )}
    </Box>
  )
}

// ─── Day agenda ───────────────────────────────────────────────────────────────

function DayAgenda({
  date,
  segments,
  tz,
  onOpenSession,
  onAddIncident,
}: {
  date: Dayjs
  segments: DaySegment[]
  tz: string
  onOpenSession: (s: SessionInfoResponse) => void
  onAddIncident: (s: SessionInfoResponse) => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dark = theme.palette.mode === 'dark'
  const hairline = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
        {date.format('dddd, D MMMM')}
      </Typography>

      {segments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t('sessions.calendar.nothingOnDay')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {segments.map((seg) => {
            const accent = ACCENT[seg.kind]
            const label = seg.session.sessionId
              ? (seg.session.title ?? t('sessions.dialog.onCallSession'))
              : t('sessions.table.unmatchedIncident')
            const total = sessionDuration(seg.session)
            const incidents = seg.session.incidents?.length ?? 0

            return (
              <ButtonBase
                key={seg.key}
                focusRipple
                onClick={() => onOpenSession(seg.session)}
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  borderRadius: 2,
                  border: `1px solid ${hairline}`,
                  borderLeft: `3px solid ${accent}`,
                  px: 1.5,
                  py: 1.25,
                  gap: 1.5,
                  alignItems: 'center',
                  transition: 'background-color 120ms ease',
                  '&:hover': { bgcolor: alpha(accent, 0.08) },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{label}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    {seg.session.endTime
                      ? `${seg.continuesBefore ? '…' : seg.from.format('HH:mm')} – ${seg.continuesAfter ? '…' : seg.to.format('HH:mm')}`
                      : seg.from.format('HH:mm')}
                    {total > 0 && ` · ${formatDuration(total)}`}
                    {seg.continuesBefore || seg.continuesAfter
                      ? ` · ${dayjs(seg.session.startTime).tz(tz).format('DD MMM HH:mm')} – ${dayjs(seg.session.endTime).tz(tz).format('DD MMM HH:mm')}`
                      : ''}
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      px: 0.875,
                      height: 22,
                      borderRadius: '999px',
                      bgcolor: incidents > 0 ? alpha(ACCENT.incident, 0.16) : 'transparent',
                      color: incidents > 0 ? ACCENT.incident : 'text.disabled',
                      border: incidents > 0 ? 'none' : `1px solid ${hairline}`,
                    }}
                  >
                    <BugReportIcon sx={{ fontSize: 13 }} />
                    <Typography component="span" sx={{ fontSize: 11, fontWeight: 700 }}>
                      {incidents}
                    </Typography>
                  </Stack>
                  <Tooltip title={t('sessions.dialog.addIncidentBtn')}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onAddIncident(seg.session) }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ButtonBase>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

// ─── Calendar ─────────────────────────────────────────────────────────────────

interface Props {
  sessions: SessionInfoResponse[]
  month: Dayjs
  onMonthChange: (month: Dayjs) => void
  tz: string
  loading?: boolean
  onOpenSession: (session: SessionInfoResponse) => void
  onAddIncident: (session: SessionInfoResponse) => void
}

export default function SessionCalendar({
  sessions,
  month,
  onMonthChange,
  tz,
  loading,
  onOpenSession,
  onAddIncident,
}: Props) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const dark = theme.palette.mode === 'dark'
  const hairline = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.09)'
  const narrowViewport = useMediaQuery(theme.breakpoints.down('sm'))
  const [gridWidth, setGridWidth] = useState(0)
  const observer = useRef<ResizeObserver | null>(null)
  const gridRef = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect()
    if (!node) return
    const ro = new ResizeObserver(([entry]) => setGridWidth(entry.contentRect.width))
    ro.observe(node)
    observer.current = ro
    setGridWidth(node.getBoundingClientRect().width)
  }, [])
  // Below ~118px a cell can no longer hold "09:00 – 21:00" plus an incident badge,
  // so the bars collapse to colour pills and the agenda underneath carries the detail.
  const compact = gridWidth > 0 ? gridWidth / 7 < 118 : narrowViewport
  const maxBars = 3

  const today = dayjs().tz(tz).startOf('day')
  const [selectedIso, setSelectedIso] = useState<string | null>(null)

  const segmentsByDay = useMemo(() => buildSegments(sessions, tz), [sessions, tz])

  const gridDays = useMemo(() => {
    const first = startOfWeekMonday(month.startOf('month'))
    const last = startOfWeekMonday(month.endOf('month')).add(6, 'day')
    const days: Dayjs[] = []
    for (let d = first; !d.isAfter(last); d = d.add(1, 'day')) days.push(d)
    return days
  }, [month])

  const weekdayNames = useMemo(() => {
    const locale = i18n.language.startsWith('ru') ? 'ru' : 'en'
    return gridDays.slice(0, 7).map((d) => d.locale(locale).format('dd'))
  }, [gridDays, i18n.language])

  // Default the agenda to today when the visible month contains it, otherwise the 1st.
  const selected = useMemo(() => {
    if (selectedIso) {
      const picked = dayjs.tz(selectedIso, tz)
      if (picked.isSame(month, 'month')) return picked
    }
    return today.isSame(month, 'month') ? today : month.startOf('month')
  }, [selectedIso, month, today, tz])

  const selectedSegments = segmentsByDay.get(selected.format('YYYY-MM-DD')) ?? []

  if (loading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={40} />
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={100} />)}
      </Stack>
    )
  }

  return (
    <Box>
      {/* Month navigation + legend */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Tooltip title={t('sessions.calendar.prevMonth')}>
            <IconButton size="small" onClick={() => onMonthChange(month.subtract(1, 'month').startOf('month'))}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('sessions.calendar.nextMonth')}>
            <IconButton size="small" onClick={() => onMonthChange(month.add(1, 'month').startOf('month'))}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            onMonthChange(dayjs().tz(tz).startOf('month'))
            setSelectedIso(dayjs().tz(tz).format('YYYY-MM-DD'))
          }}
          sx={{ minWidth: 0, color: 'text.secondary' }}
        >
          {t('sessions.calendar.today')}
        </Button>

        <Stack
          direction="row"
          spacing={1.5}
          sx={{ ml: 'auto', flexWrap: 'wrap', rowGap: 0.5 }}
        >
          {([
            ['session', t('sessions.calendar.legend.session')],
            ['incident', t('sessions.calendar.legend.incident')],
          ] as [SessionKind, string][]).map(([kind, label]) => (
            <Stack key={kind} direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ACCENT[kind] }} />
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* Grid */}
      <Box sx={{ border: `1px solid ${hairline}`, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            borderBottom: `1px solid ${hairline}`,
            bgcolor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
          }}
        >
          {weekdayNames.map((name, i) => (
            <Typography
              key={name + i}
              sx={{
                py: 0.75,
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: i >= 5 ? 'text.disabled' : 'text.secondary',
              }}
            >
              {name}
            </Typography>
          ))}
        </Box>

        <Box
          ref={gridRef}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            // Hairlines drawn by the gap showing the container background through.
            gap: '1px',
            bgcolor: hairline,
            '& > *': { bgcolor: 'background.paper' },
          }}
        >
          {gridDays.map((d) => {
            const iso = d.format('YYYY-MM-DD')
            return (
              <DayCell
                key={iso}
                date={d}
                segments={segmentsByDay.get(iso) ?? []}
                inMonth={d.isSame(month, 'month')}
                isToday={d.isSame(today, 'day')}
                isSelected={d.isSame(selected, 'day')}
                compact={compact}
                maxBars={maxBars}
                tz={tz}
                onSelect={() => setSelectedIso(iso)}
                onOpenSession={onOpenSession}
              />
            )
          })}
        </Box>
      </Box>

      <DayAgenda
        date={selected}
        segments={selectedSegments}
        tz={tz}
        onOpenSession={onOpenSession}
        onAddIncident={onAddIncident}
      />
    </Box>
  )
}
